<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="spell-checker-page">
    <!-- SEO-Optimized Hero Section -->
    <header class="hero">
      <div class="container">
        <h1 class="hero-title">{{ $t('seo.spellChecker.title') }}</h1>
        <p class="hero-subtitle">{{ $t('seo.spellChecker.description') }}</p>
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            {{ $t('spellChecker.checkNow') }}
          </router-link>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('spellChecker.viewPricing') }}
          </router-link>
        </div>
        <div class="trust-indicators">
          <span class="trust-item">✓ {{ $t('spellChecker.trust.accurate') }}</span>
          <span class="trust-item">✓ {{ $t('spellChecker.trust.instant') }}</span>
          <span class="trust-item">✓ {{ $t('spellChecker.trust.secure') }}</span>
        </div>
      </div>
    </header>

    <!-- Accuracy Showcase Section -->
    <section class="accuracy-showcase">
      <div class="container">
        <h2>{{ $t('spellChecker.accuracy.title') }}</h2>
        <div class="accuracy-stats">
          <div class="stat-card">
            <div class="stat-number">99.8%</div>
            <div class="stat-label">{{ $t('spellChecker.accuracy.rate') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">3</div>
            <div class="stat-label">{{ $t('spellChecker.accuracy.languages') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">0.1s</div>
            <div class="stat-label">{{ $t('spellChecker.accuracy.speed') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features">
      <div class="container">
        <h2>{{ $t('spellChecker.features.title') }}</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🎯</div>
            <h3>{{ $t('spellChecker.features.contextual.title') }}</h3>
            <p>{{ $t('spellChecker.features.contextual.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🌍</div>
            <h3>{{ $t('spellChecker.features.multilingual.title') }}</h3>
            <p>{{ $t('spellChecker.features.multilingual.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔒</div>
            <h3>{{ $t('spellChecker.features.privacy.title') }}</h3>
            <p>{{ $t('spellChecker.features.privacy.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>{{ $t('spellChecker.features.realtime.title') }}</h3>
            <p>{{ $t('spellChecker.features.realtime.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison Section -->
    <section class="comparison">
      <div class="container">
        <h2>{{ $t('spellChecker.comparison.title') }}</h2>
        <div class="comparison-table">
          <div class="comparison-header">
            <div class="feature-col">{{ $t('spellChecker.comparison.feature') }}</div>
            <div class="noorwriter-col">n.writer</div>
            <div class="competitor-col">{{ $t('spellChecker.comparison.traditional') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('spellChecker.comparison.features.aiPowered') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">❌</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('spellChecker.comparison.features.contextAware') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">❌</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">
              {{ $t('spellChecker.comparison.features.multiLanguage') }}
            </div>
            <div class="noorwriter-col">✅ {{ $t('spellChecker.trust.multilingual') }}</div>
            <div class="competitor-col">❌ {{ $t('spellChecker.comparison.limited') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('spellChecker.comparison.features.suggestions') }}</div>
            <div class="noorwriter-col">✅ {{ $t('spellChecker.comparison.intelligent') }}</div>
            <div class="competitor-col">❌ {{ $t('spellChecker.comparison.basic') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section for Voice Search -->
    <section class="faq">
      <div class="container">
        <h2>{{ $t('spellChecker.faq.title') }}</h2>
        <div class="faq-list">
          <div class="faq-item">
            <h3>{{ $t('spellChecker.faq.q1.question') }}</h3>
            <p>{{ $t('spellChecker.faq.q1.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('spellChecker.faq.q2.question') }}</h3>
            <p>{{ $t('spellChecker.faq.q2.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('spellChecker.faq.q3.question') }}</h3>
            <p>{{ $t('spellChecker.faq.q3.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('spellChecker.faq.q4.question') }}</h3>
            <p>{{ $t('spellChecker.faq.q4.answer') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('spellChecker.cta.title') }}</h2>
        <p>{{ $t('spellChecker.cta.description') }}</p>
        <router-link to="/editor" class="btn btn-primary btn-large">
          {{ $t('spellChecker.cta.button') }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// SEO optimization for Spell Checker page
useHead({
  title: () => t('seo.spellChecker.title'),
  meta: [
    {
      name: 'description',
      content: () => t('seo.spellChecker.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.spellChecker.keywords'),
    },
    {
      property: 'og:title',
      content: () => t('seo.spellChecker.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.spellChecker.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app/spell-checker',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.spellChecker.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.spellChecker.description'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://n-writer.app/spell-checker',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('seo.spellChecker.title'),
        description: t('seo.spellChecker.description'),
        url: 'https://n-writer.app/spell-checker',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'n.writer Spell Checker',
          applicationCategory: 'WritingApplication',
          operatingSystem: 'Web Browser',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          featureList: [
            'Spell Checking',
            'Contextual Corrections',
            'Multilingual Support',
            'Real-time Detection',
          ],
        },
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('spellChecker.faq.q1.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('spellChecker.faq.q1.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('spellChecker.faq.q2.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('spellChecker.faq.q2.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('spellChecker.faq.q3.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('spellChecker.faq.q3.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('spellChecker.faq.q4.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('spellChecker.faq.q4.answer'),
            },
          },
        ],
      }),
    },
    // HowTo Schema for Spell Checker
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('spellChecker.howItWorks.title'),
        description: t('geo.definitions.spellChecker'),
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('spellChecker.howItWorks.step1.title'),
            text: t('spellChecker.howItWorks.step1.description'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('spellChecker.howItWorks.step2.title'),
            text: t('spellChecker.howItWorks.step2.description'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('spellChecker.howItWorks.step3.title'),
            text: t('spellChecker.howItWorks.step3.description'),
          },
        ],
      }),
    },
  ],
})
</script>

<style lang="scss" scoped>
.spell-checker-page {
  min-height: 100vh;
}

.hero {
  background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  // Animated orbs
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
    animation: float 8s ease-in-out infinite;
  }

  &::before {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, #1E40AF 0%, transparent 70%);
    top: -100px;
    right: -50px;
  }

  &::after {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, #3B82F6 0%, transparent 70%);
    bottom: -50px;
    left: -50px;
    animation-delay: -3s;
  }

  .hero-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;
    background: linear-gradient(135deg, #FFFFFF 0%, #CBD5E1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    z-index: 1;
  }

  .hero-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    color: #CBD5E1;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }

  .hero-actions {
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;

    .btn {
      margin: 0 0.5rem;
    }
  }

  .trust-indicators {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;

    .trust-item {
      font-size: 0.9rem;
      color: #3B82F6;
      background: rgba(30, 64, 175, 0.1);
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      border: 1px solid rgba(59, 130, 246, 0.2);
    }
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.75rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &-primary {
    background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
    color: white;
    box-shadow: 0 4px 20px rgba(30, 64, 175, 0.4);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(30, 64, 175, 0.5);
    }
  }

  &-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: #3B82F6;
      transform: translateY(-3px);
    }
  }

  &-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }
}

.accuracy-showcase {
  padding: 5rem 0;
  background: linear-gradient(135deg, #EFF6FF 0%, #CCFBF1 100%);
  
  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: #1E3A8A;
  }
}

.accuracy-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.stat-card {
  text-align: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(30, 64, 175, 0.1);

  .stat-number {
    font-size: 3rem;
    font-weight: 700;
    background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    font-size: 1rem;
    color: #64748B;
  }
}

.features,
.comparison,
.language-support,
.faq,
.cta {
  padding: 5rem 0;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: var(--nw-brand-slate, #1E293B);
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
}

.feature-card {
  padding: 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(30, 64, 175, 0.1);
  border-radius: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(30, 64, 175, 0.15);
    border-color: rgba(30, 64, 175, 0.3);
  }

  .feature-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  h3 {
    margin-bottom: 1rem;
    color: #1E40AF;
  }

  p {
    color: #64748B;
    line-height: 1.6;
  }
}

.comparison {
  background: #F8FAFC;
}

.comparison-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0;
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);

  .comparison-header {
    display: contents;
    font-weight: 600;

    > div {
      background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
      color: white;
      padding: 1.25rem;
    }
  }

  .comparison-row {
    display: contents;

    > div {
      padding: 1.25rem;
      border-bottom: 1px solid #E5E7EB;
      background: white;
    }

    &:last-child > div {
      border-bottom: none;
    }
  }

  .noorwriter-col {
    text-align: center;
    color: #1E40AF;
    font-weight: 600;
  }

  .competitor-col {
    text-align: center;
    color: #6B7280;
  }
}

.languages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.language-group {
  h3 {
    margin-bottom: 1rem;
    color: #1E40AF;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      padding: 0.5rem 0;
      border-bottom: 1px solid #E5E7EB;
      color: #64748B;

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.faq {
  background: #F8FAFC;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  margin-bottom: 1.5rem;
  padding: 1.5rem 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  h3 {
    margin-bottom: 0.75rem;
    color: #1E293B;
    font-size: 1.1rem;
  }

  p {
    color: #64748B;
    line-height: 1.7;
    margin: 0;
  }
}

.cta {
  background: linear-gradient(135deg, #EFF6FF 0%, #CCFBF1 100%);
  text-align: center;

  h2 {
    color: #1E3A8A;
  }

  p {
    color: #172554;
    font-size: 1.125rem;
    margin-bottom: 2rem;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 3rem 0;

    .hero-title {
      font-size: 2rem;
    }
  }

  .comparison-table {
    grid-template-columns: 1fr;
    border-radius: 1rem;

    .comparison-header {
      display: none;
    }
  }

  .hero .trust-indicators {
    flex-direction: column;
    gap: 0.75rem;
  }

  .accuracy-stats {
    grid-template-columns: 1fr;
  }

  .features,
  .comparison,
  .faq,
  .cta,
  .accuracy-showcase {
    padding: 3rem 0;

    h2 {
      font-size: 1.75rem;
    }
  }
}
</style>
